import React from "react";
import {
  ClassicLadderTournament,
  ClassicLadderMatch,
  Team,
} from "../types/tournament";
import { Trophy, Users, ArrowUp, ArrowDown, Crown } from "lucide-react";

interface ClassicLadderDisplayProps {
  tournament: ClassicLadderTournament;
  standings: Team[];
  onMatchClick: (match: ClassicLadderMatch) => void;
}

const ClassicLadderDisplay: React.FC<ClassicLadderDisplayProps> = ({
  tournament,
  standings,
  onMatchClick,
}) => {
  const currentRound = tournament.rounds.find(
    (round) => round.roundNumber === tournament.currentRound
  );

  // Group teams by court for display
  const teamsByCourt: { [court: number]: Team[] } = {};
  standings.forEach((team) => {
    if (!teamsByCourt[team.currentCourt]) {
      teamsByCourt[team.currentCourt] = [];
    }
    teamsByCourt[team.currentCourt].push(team);
  });

  // Get matches for current round by court
  const matchesByCourt: { [court: number]: ClassicLadderMatch[] } = {};
  currentRound?.matches.forEach((match) => {
    if (!matchesByCourt[match.court]) {
      matchesByCourt[match.court] = [];
    }
    matchesByCourt[match.court].push(match);
  });

  const getTeamMovement = (team: Team) => {
    const movement = team.startingCourt - team.currentCourt;
    if (movement > 0) return { direction: "up", amount: movement };
    if (movement < 0) return { direction: "down", amount: Math.abs(movement) };
    return { direction: "same", amount: 0 };
  };

  const renderCourt = (courtNumber: number) => {
    const courtTeams = teamsByCourt[courtNumber] || [];
    const courtMatches = matchesByCourt[courtNumber] || [];
    const isCourt1 = courtNumber === 1;

    return (
      <div
        key={courtNumber}
        className={`bg-white rounded-lg shadow-lg p-6 border-2 ${
          isCourt1
            ? "border-yellow-400 bg-gradient-to-br from-yellow-50 to-amber-50"
            : "border-gray-300"
        }`}
      >
        {/* Court Header */}
        <div className="text-center mb-4">
          <h3
            className={`text-lg font-bold ${
              isCourt1 ? "text-yellow-700" : "text-gray-900"
            }`}
          >
            Court {courtNumber}{" "}
            {isCourt1 && <Crown className="inline w-5 h-5 ml-1" />}
          </h3>
          {isCourt1 && (
            <p className="text-sm text-yellow-600 font-medium">
              Championship Court
            </p>
          )}
        </div>

        {/* Teams on Court */}
        <div className="space-y-3 mb-4">
          {courtTeams.map((team) => {
            const movement = getTeamMovement(team);
            return (
              <div
                key={team.id}
                className={`p-3 rounded-lg border ${
                  isCourt1
                    ? "bg-yellow-100 border-yellow-300"
                    : "bg-gray-50 border-gray-200"
                } hover:shadow-md transition-all duration-200`}
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="font-semibold text-gray-900">
                      {team.name}
                    </div>
                    <div className="text-sm text-gray-600">
                      {team.player1} & {team.player2}
                    </div>
                    <div className="text-xs text-blue-600 mt-1">
                      {team.wins}W - {team.losses}L
                    </div>
                  </div>

                  {/* Movement Indicator */}
                  <div className="text-right">
                    {movement.direction === "up" && (
                      <div className="flex items-center text-green-600">
                        <ArrowUp className="w-4 h-4 mr-1" />
                        <span className="text-xs">+{movement.amount}</span>
                      </div>
                    )}
                    {movement.direction === "down" && (
                      <div className="flex items-center text-red-600">
                        <ArrowDown className="w-4 h-4 mr-1" />
                        <span className="text-xs">-{movement.amount}</span>
                      </div>
                    )}
                    {movement.direction === "same" && (
                      <div className="text-xs text-gray-500">Start</div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}

          {courtTeams.length === 0 && (
            <div className="text-center py-4 text-gray-500 italic">
              No teams on this court
            </div>
          )}
        </div>

        {/* Current Matches */}
        {courtMatches.length > 0 && (
          <div className="space-y-3">
            <h4 className="font-semibold text-gray-900 text-sm border-t pt-3">
              Current Match{courtMatches.length > 1 ? "es" : ""}
            </h4>
            {courtMatches.map((match) => (
              <div
                key={match.id}
                className={`p-3 rounded-lg border-2 cursor-pointer transition-all duration-200 hover:shadow-md ${
                  match.isCompleted
                    ? "bg-green-50 border-green-300"
                    : isCourt1
                    ? "bg-yellow-200 border-yellow-400 hover:bg-yellow-300"
                    : "bg-blue-50 border-blue-300 hover:bg-blue-100"
                }`}
                onClick={() => !match.isCompleted && onMatchClick(match)}
              >
                <div className="space-y-2">
                  <div className="text-center">
                    <div className="font-medium text-gray-900">
                      {match.team1.name}
                    </div>
                    <div className="text-xs text-gray-600">
                      {match.team1.wins}W - {match.team1.losses}L
                    </div>
                  </div>

                  <div className="text-center text-gray-500 text-sm font-medium">
                    {match.result ? (
                      <div className="space-y-1">
                        <div className="font-bold">
                          {match.result.team1Score} - {match.result.team2Score}
                        </div>
                        <div className="text-xs text-green-600">✓ Complete</div>
                      </div>
                    ) : (
                      <div>
                        VS
                        {isCourt1 && (
                          <div className="text-xs text-yellow-700 font-semibold">
                            👑 COURT 1 BATTLE!
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="text-center">
                    <div className="font-medium text-gray-900">
                      {match.team2.name}
                    </div>
                    <div className="text-xs text-gray-600">
                      {match.team2.wins}W - {match.team2.losses}L
                    </div>
                  </div>
                </div>

                {!match.isCompleted && (
                  <div className="mt-2 text-center">
                    <span
                      className={`text-xs px-2 py-1 rounded-full ${
                        isCourt1
                          ? "bg-yellow-300 text-yellow-800"
                          : "bg-blue-200 text-blue-800"
                      }`}
                    >
                      Click to Enter Score
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-8">
      {/* Tournament Header */}
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          🏆 Classic Speed Ladder
        </h2>
        <p className="text-gray-600">
          {tournament.teams.length} Teams • {tournament.courts.length} Courts •
          Round {tournament.currentRound} of {tournament.totalRounds}
        </p>

        {/* Court 1 Defender Display */}
        {tournament.court1Defender && (
          <div className="mt-4 inline-block bg-yellow-100 border border-yellow-300 rounded-lg px-4 py-2">
            <div className="flex items-center space-x-2">
              <Crown className="w-5 h-5 text-yellow-600" />
              <div>
                <div className="font-semibold text-yellow-800">
                  {tournament.court1Defender.name}
                </div>
                <div className="text-sm text-yellow-700">
                  Defending Court 1 for {tournament.court1DefenderStreak} round
                  {tournament.court1DefenderStreak !== 1 ? "s" : ""}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Ladder Display */}
      <div className="space-y-6">
        <h3 className="text-xl font-semibold text-gray-900 text-center mb-6">
          Current Ladder Positions
        </h3>

        {/* Courts Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {Array.from(
            { length: tournament.courts.length },
            (_, i) => i + 1
          ).map((courtNumber) => renderCourt(courtNumber))}
        </div>
      </div>

      {/* Legend */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="font-semibold text-gray-900 mb-2">
          How Classic Speed Ladder Works
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <ArrowUp className="w-4 h-4 text-green-600" />
              <span>
                <strong>Winners move UP</strong> (to lower court numbers)
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <ArrowDown className="w-4 h-4 text-red-600" />
              <span>
                <strong>Losers move DOWN</strong> (to higher court numbers)
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <Crown className="w-4 h-4 text-yellow-600" />
              <span>
                <strong>Court 1 winners</strong> stay and defend
              </span>
            </div>
          </div>
          <div className="space-y-2">
            <div>
              • <strong>Goal:</strong> Reach and defend Court 1
            </div>
            <div>
              • <strong>Everyone plays:</strong> No eliminations
            </div>
            <div>
              • <strong>Recovery possible:</strong> Bad start? Climb back up!
            </div>
            <div>
              • <strong>Final champion:</strong> Team on Court 1 at the end
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClassicLadderDisplay;
